import api from "@core/plugins/api";
import { RECRUITMENT_TYPES } from "@core/constants";
import { ExtraModel, RecruitmentFactory, Recruitment } from "@core/models";
import { ArrayHelpers } from "@core/helpers";
import { isWeb } from "@core/plugins/mobile";
import { ExtraBlacklistDTO } from "@core/dto";
import {
  GetCandidateProfileBio,
  GetCandidateProfileLanguages,
  GetCandidateProfileSkills,
  GetCandidateProfileCourses,
  GetCandidateProfileExperiences,
} from "@extracadabra/core-v2";
import { GetBoCandidateVerifiedStatus } from "@extracadabra/core-v2";

const profileCandidateUrl = (extraId, demandId, type) => `/api/et/profil/extra/recruitment/${extraId}/${demandId}/${type}`;
const getByKeywordUrl = (keyword) => `/api/extra/autocomplete?search=${keyword}`;
const downloadResumeUrl = (extraId, demandId) => `/api/et/cv/${demandId}/${extraId}`;
const getBlackListedExtra = (id) => `/blacklist-extra/bo/blacklist-extra/${id}`;
const findExtraOnBackOfficeUrl = (slug) => `api/extrabizdev/find?q=${slug}`;
const addExtraOnBlacklistUrl = () => `/blacklist-extra/bo/blacklist-extra`;
const deleteExtraOnBlackListUrl = (extraId, etabId) => `/blacklist-extra/bo/blacklist-extra/${extraId}/${etabId}`;
const getExtraByIdUrl = (slug) => `api/extrabizdev/extra/${slug}`;
const getSkillsByIdUrl = (candidateId) => `/et/candidate-jo-skill/${candidateId}`;

export class ExtraService {
  static async getById({ extraId, demandId, type = RECRUITMENT_TYPES.PERMANENT.label }) {
    try {
      const response = await api.private.get(profileCandidateUrl(extraId, demandId, type));
      const profile = response && response.data;
      return profile;
    } catch (error) {
      throw error;
    }
  }
  static async getBioById({ repository, candidateId, demandId }) {
    try {
      const response = await new GetCandidateProfileBio(repository).execute({ jobId: demandId, id: candidateId });
      return response;
    } catch (error) {
      throw error;
    }
  }
  static async getLanguageById({ repository, candidateId, demandId }) {
    try {
      const response = await new GetCandidateProfileLanguages(repository).execute({ jobId: demandId, id: candidateId });
      return response;
    } catch (error) {
      throw error;
    }
  }
  static async getSkills({ repository, candidateId, demandId }) {
    try {
      const response = await new GetCandidateProfileSkills(repository).execute({ jobId: demandId, id: candidateId });
      return response;
    } catch (error) {
      throw error;
    }
  }
  static async getCourses({ repository, candidateId, demandId }) {
    try {
      const response = await new GetCandidateProfileCourses(repository).execute({ jobId: demandId, id: candidateId });
      return response;
    } catch (error) {
      throw error;
    }
  }
  static async getExperiences({ repository, candidateId, demandId }) {
    try {
      const response = await new GetCandidateProfileExperiences(repository).execute({ jobId: demandId, id: candidateId });
      return response;
    } catch (error) {
      throw error;
    }
  }
  static async getByKeyword(keyword) {
    try {
      if (keyword) {
        const response = await api.private.get(getByKeywordUrl(keyword));
        return response && ArrayHelpers.ensureArray(response.data);
      }
      return [];
    } catch (error) {
      throw error;
    }
  }

  static async downloadResume({ extraId, demandId, extraIdentity }) {
    try {
      const response = await api.private.get(downloadResumeUrl(extraId, demandId), { responseType: "blob" });
      const resume = response && response.data;

      if (isWeb) {
        const pdfBlob = new Blob([resume], { type: "application/pdf" });
        const fileName = `extracadabra-cv-${extraIdentity.lastname}_${extraIdentity.firstname}.pdf`;
        const fileURL = window.URL.createObjectURL(pdfBlob);

        const anchor = document.createElement("a");
        anchor.href = fileURL;
        anchor.download = fileName;
        anchor.click();
      }
    } catch (error) {
      throw error;
    }
  }

  static canAccessContactInformations({ extra, recruitment }) {
    let shouldAccess = false;

    if (!(extra instanceof ExtraModel)) {
      extra = new ExtraModel(extra);
    }

    if (!(recruitment instanceof Recruitment)) {
      recruitment = new RecruitmentFactory(recruitment);
    }

    if (recruitment.isPermanent()) {
      shouldAccess = ExtraService.shouldAccessContactInformationsForPermanentDemand({ extra });
    }

    if (recruitment.isTemporary()) {
      shouldAccess = ExtraService.shouldAccessContactInformationsForTemporaryDemand({ extra, recruitment });
    }

    return shouldAccess && !recruitment.isArchived();
  }

  static shouldAccessContactInformationsForPermanentDemand({ extra }) {
    return extra.isSelected() || extra.hasRefused();
  }

  static shouldAccessContactInformationsForTemporaryDemand({ extra, recruitment }) {
    return (extra.isSelected() || extra.isCanceled()) && !recruitment.hasEnded();
  }

  static async getBlackListedExtraUrl(id) {
    try {
      const response = await api.private.get(getBlackListedExtra(id), { cache: false });
      const extra = response && ArrayHelpers.ensureArray(response.data);
      return extra;
    } catch (error) {
      throw error;
    }
  }

  static async findExtraOnBackOffice(slug) {
    try {
      const response = await api.private.get(findExtraOnBackOfficeUrl(slug), { cache: false });
      const extra = response && ArrayHelpers.ensureArray(response.data);
      return extra;
    } catch (error) {
      throw error;
    }
  }

  static async addExtraOnBlacklist({ etabId, extraId }) {
    try {
      const dto = new ExtraBlacklistDTO({ etabId, extraId });
      const data = dto.prepareData();
      const response = await api.private.post(addExtraOnBlacklistUrl(), data);
      return response && response.data;
    } catch (error) {
      throw error;
    }
  }

  static async deleteExtraOnBlackList({ etabId, extraId }) {
    try {
      const response = await api.private.delete(deleteExtraOnBlackListUrl(extraId, etabId));
      return response;
    } catch (error) {
      throw error;
    }
  }

  static async getExtraById(slug) {
    try {
      const response = await api.private.get(getExtraByIdUrl(slug));
      const extra = response && response.data;
      return extra;
    } catch (error) {
      throw error;
    }
  }

  static async getSkillsById({ candidateId }) {
    try {
      const response = await api.private.get(getSkillsByIdUrl(candidateId));
      const skills = response && response.data;
      return skills;
    } catch (error) {
      throw error;
    }
  }
  static async getVerifiedStatus({ extraId, repository }) {
    try {
      const verifiedStatus = await new GetBoCandidateVerifiedStatus(repository).execute(extraId);
      return verifiedStatus;
    } catch (error) {
      throw error;
    }
  }
}
